/**
 * Copyright (c) 2026, CYBRA Corp. All rights reserved.
 */
import { Component, OnInit, ViewChild } from "@angular/core";
import { ActivatedRoute, Router } from "@angular/router";
import { ERROR_DIALOG_TITLE, SimpleSuccessErrorDialogComponent } from "app/components/simple-success-error-dialog-box/simple-success-error-dialog-box.component";
import { ServiceInstance } from "app/models/service-instance";
import { CookieService } from "ngx-cookie-service";

const CookieKey_SelectedService = "page[pvh.scan-chamber.select-chamber]-key[selected-id]";

/**
 * "Select Chamber" page for PVH's custom "Scan Chamber" process. 
 * Allows the User to select the "Scan Chamber" they want to view / use. 
 * Stores the selected Service as a cookie so that it can be auto-selected the next time. 
 */
@Component({
    templateUrl: './pvh.scan-chamber.select-chamber.component.html',
    styleUrls: ['./pvh.scan-chamber.select-chamber.component.css']
  })
export class PvhScanChamberSelectChamberComponent implements OnInit {

  /** Reference to the SimpleSuccessErrorDialog used to display error */
  @ViewChild('successErrorDialog') 
  simpleSuccessErrorDialogComponent!: SimpleSuccessErrorDialogComponent;

  /** Portals available for the User to select from */
  availableServices: ServiceInstance[] = [];

  /** Currently selected Portal in the UI */
  selectedService: ServiceInstance | null = null;

  /** Holds whether the UI should be disabled or not (done while communicating to the backend) */
  isUiDisabled = false;


  /** Constructor */
  constructor(
    private cookieService: CookieService,
    private route: ActivatedRoute,
    private router: Router
  ) { }

  /**
   * Angular Lifecycle hook.  Called after the component is created, but 
   * before the views are created.
   * 
   * Grabs the data that was obtained while the page was loading and 
   * updates the current displayed data.
   */
  ngOnInit(): void {
    this.route.data.subscribe(data => {
      // Update the available services
      const availableServices = (data['availableServices'] as ServiceInstance[])
      if (availableServices?.length > 0) {
        this.availableServices.push(...availableServices);
      }

      // Auto-select the previously selected one (if available)
      const selectedId = this.getStoredServiceId();
      const selectedService = this.availableServices.find(service => service?.id === selectedId);
      this.selectedService = selectedService? selectedService : null;
    });
  }

  /**
   * Called when the "Select" button is clicked in the UI.
   * Selects the selected Service and forwards the User to the "Enter Carton" screen.
   */
  onSelectServiceClicked(): void {
    console.log(`onSelectPortalClicked() - Is UI Disabled: ${this.isUiDisabled}`);
    if (!this.isUiDisabled) {
      this.isUiDisabled = true;
      this.selectService();
    }
  }

  /**
   * Async Task for selecting the Service. 
   * Stores the selected Service's ID for auto-selecting next time and forwards the User to the 
   * "Enter Carton" screen.
   */
  private async selectService(): Promise<void> {
    console.log(`selectService() - Selected Service: ${this.selectedService}`);
    if (this.selectedService?.id) {
      const selectedService = this.selectedService.id;
      this.setStoredServiceId(selectedService);

      this.router.navigate(['pvh', 'scan-chamber', selectedService, 'dashboard'], {});
      this.isUiDisabled = false;
    }
  }

  /**
   * Called to handle errors when calling APIs.  
   * Currently this prints them out to the console and displays an alert to the User.
   */
  handleError(error: any): void {
    if (error?.error != null) {
      this.simpleSuccessErrorDialogComponent.showErrorDialog(ERROR_DIALOG_TITLE, error.error);
    }
    /* Default back to old handling */
    else if (error != null) {
      const response: Response = error;
    }
  }

  /**
   * Returns the stored "Service ID" value from the Cookie.
   * This is used to auto-select the previously selected Portal (if available).
   */
  private getStoredServiceId(): number | null {
    const value = this.cookieService.get(CookieKey_SelectedService);
    if (value == null || value == undefined || value.trim().length == 0) {
      return null;
    } else {
      const storedValue: StoredInfo = JSON.parse(value);
      return storedValue.service_id;
    }
    
  }

  /**
   * Sets the stored "Service ID" value to the Cookie.
   * This is used in the auto-select logic to have the previously selected Service 
   * auto-selected (if available).
   */
  private setStoredServiceId(id: number): void {
    const value = { service_id: id } as StoredInfo;
    this.cookieService.set(CookieKey_SelectedService, JSON.stringify(value), 1, '/', undefined, false, 'Lax');
  }

}

/**
 * Interface representing the stored "Portal ID" value in the Cookie as JSON.
 */
interface StoredInfo {
  service_id: number;
}
